<script lang="ts">
	import { locale, localizeHref, englishPath, localeFromPath } from '$lib/localization/locale';
	import { message } from '$lib/localization/messages';

	import { onMount, onDestroy } from 'svelte';
	import { navigating, page } from '$app/stores';
	import type { User } from 'firebase/auth';
	import authStore from '$lib/authStore';
	import Header from '$components/Header.svelte';
	import LanguageSelector from '$lib/localization/LanguageSelector.svelte';
	import LoginOrSignUpModal from '$components/LoginOrSignUpModal.svelte';
	import { analytics } from '$lib/analytics';
	import { browser } from '$app/environment';
	import { logLandingBoot } from '$lib/landingBootLog';
	import { IS_AQUEDUCT } from '$lib/site';

	logLandingBoot('LandingPageLayout: component script evaluated');

	let subscriptions: (() => void)[] = [];
	// Locale routes can remount this layout. Preserve the initial-load fade,
	// but show the already-resolved login control immediately on a language switch.
	const fromUrl: URL | undefined = $navigating?.from?.url;
	const toUrl: URL | undefined = $navigating?.to?.url;
	const animateLogin: boolean = !(
		IS_AQUEDUCT &&
		fromUrl &&
		toUrl &&
		localeFromPath(fromUrl.pathname) !== localeFromPath(toUrl.pathname) &&
		englishPath(fromUrl.pathname) === englishPath(toUrl.pathname)
	);
	export let user: User | null | undefined = undefined;

	export let redirectAfterLogin: string | undefined = undefined;
	export let loginOrSignUpModal: LoginOrSignUpModal;
	export let loginOrSignupWhichModal: 'login' | 'sign-up' = 'sign-up';
	export let loginTitleMessage: string = 'Log in to Iliad to continue.';
	export let signUpTitleMessage: string = IS_AQUEDUCT
		? 'Create an Aqueduct account to continue.'
		: 'Create a free account to use Iliad.';
	export let onAuthenticated:
		| ((user: User, isNewUser: boolean) => void | Promise<void>)
		| undefined = undefined;

	onMount(async () => {
		const startedAt = performance.now();
		logLandingBoot('LandingPageLayout: onMount start', {
			documentReadyState: document.readyState
		});
		if (!browser) {
			return;
		}

		if ($page.url.pathname) {
			localStorage.setItem('redirectUrl', $page.url.pathname);
		}

		document.body.style.overflowY = 'auto';

		subscriptions.push(
			authStore.subscribe(async (auth) => {
				logLandingBoot('LandingPageLayout: authStore update', {
					hasUser: !!auth.user,
					isLoggedIn: auth.isLoggedIn,
					firebaseControlled: auth.firebaseControlled
				});
				user = auth.user;

				// we don't know their sign-in status yet
				if (user === undefined) {
					return;
				}

				// user is not logged in
				if (user === null) {
					return;
				}

				if (user.email) {
					analytics.identify(user.email, {
						email: user.email,
						name: user.displayName
					});
				}
			})
		);

		logLandingBoot('LandingPageLayout: onMount complete', {
			duration: Number((performance.now() - startedAt).toFixed(1)),
			subscriptions: subscriptions.length
		});
	});

	onDestroy(() => {
		for (const _ of subscriptions) {
			_();
		}
	});
</script>

<div class="w-screen min-h-screen h-fit bg-primary-100 dark:bg-zinc-900 overflow-x-hidden">
	<div id="page" class="w-screen h-fit flex flex-col landing-page-gradient">
		<div class="w-full h-12 p-2 flex flex-row justify-between">
			<div class="flex items-center gap-4">
				<div class="flex items-center gap-2">
					<Header />
					{#if IS_AQUEDUCT && englishPath($page.url.pathname) === '/'}<LanguageSelector />{/if}
				</div>
				{#if IS_AQUEDUCT}
					<nav
						class="flex items-center gap-4 text-xs text-zinc-600 dark:text-zinc-400"
						aria-label={message($locale, 'common/LandingPageLayout', 'accountAndPricing')}
					>
						<a
							href={localizeHref($locale, '/pricing')}
							class="hover:brightness-90 dark:hover:brightness-150"
						>
							{message($locale, 'common/LandingPageLayout', 'pricing')}
						</a>
						{#if user === null}
							<a
								href={localizeHref($locale, '/login')}
								data-cold-trace="header-log-in"
								class:auth-actions-fade={animateLogin}
								class="hover:brightness-90 dark:hover:brightness-150"
							>
								{message($locale, 'common/LandingPageLayout', 'logIn')}
							</a>
						{:else}
							<span class="invisible" aria-hidden="true"
								>{message($locale, 'common/LandingPageLayout', 'logIn')}</span
							>
						{/if}
					</nav>
				{/if}
			</div>
		</div>
		<div class="w-full h-fit flex flex-col justify-start items-center">
			<slot></slot>
		</div>
	</div>
</div>

<LoginOrSignUpModal
	{redirectAfterLogin}
	{onAuthenticated}
	bind:loginTitleMessage
	bind:signUpTitleMessage
	bind:this={loginOrSignUpModal}
	bind:whichModal={loginOrSignupWhichModal}
/>

<style>
	.auth-actions-fade {
		opacity: 0;
		animation: auth-actions-fade 150ms ease-out forwards;
	}

	@keyframes auth-actions-fade {
		to {
			opacity: 1;
		}
	}

	@media (prefers-reduced-motion: reduce) {
		.auth-actions-fade {
			opacity: 1;
			animation: none;
		}
	}
</style>
